import type { GetCasesAnalyticsResponse } from '@compass/client/types/cases_analytics';

import { CHART_COLOR_VARS, colorForSeriesIndex } from '@/pages/analytics/utils/chartPalette';

type Grouped = { group?: string }[] | undefined;

type Dimension = 'by_report_type' | 'by_program' | 'by_submission_method';

/** Every list of the response that carries group keys, under the dimension it groups by. */
const listsByDimension = (response: GetCasesAnalyticsResponse): Record<Dimension, Grouped[]> => ({
  by_report_type: [
    response.submitted_trend?.by_report_type,
    response.time_to_submit?.by_report_type,
    response.breakdown?.by_report_type,
  ],
  by_program: [
    response.submitted_trend?.by_program,
    response.time_to_submit?.by_program,
    response.breakdown?.by_program,
    // One line per program under the SLA compliance chart's By Program scope.
    response.sla_compliance_trend,
  ],
  by_submission_method: [
    response.submitted_trend?.by_submission_method,
    response.time_to_submit?.by_submission_method,
    response.breakdown?.by_submission_method,
  ],
});

/**
 * One colour per group key (a program, report type or submission method) across every chart of
 * the page, so a program keeps its colour in the trend, the donut and the histogram. Keys are
 * ordered by name, so the colour does not depend on which chart or filter lists them first.
 *
 * Each dimension gets its own run of the theme chart colours, starting at the first one, so the
 * page reads like QC Analytics: every categorical series there is a `--chart-N` token, and the
 * high-contrast palette only takes over past the five tokens, as DonutBreakdown already does.
 * Numbering the dimensions together instead would push a two-series chart such as AE / PQC onto
 * whichever colours its keys happened to sort into among the programs.
 */
export const buildGroupColors = (
  response: GetCasesAnalyticsResponse | undefined,
): Map<string, string> => {
  const colors = new Map<string, string>();
  if (!response) return colors;

  for (const lists of Object.values(listsByDimension(response))) {
    const keys = new Set<string>();
    for (const list of lists) {
      for (const row of list ?? []) if (row.group) keys.add(row.group);
    }
    keys.delete('overall');
    const themed = keys.size <= CHART_COLOR_VARS.length;
    [...keys]
      .sort()
      .forEach((key, index) =>
        colors.set(key, themed ? CHART_COLOR_VARS[index] : colorForSeriesIndex(index)),
      );
  }
  return colors;
};
